/**
 * The main menu is data, not code.
 *
 * `webreports.WEBObjects` holds every page of every portal — id, parent, icon,
 * translation key, order, and since the unified shell also the application the
 * page belongs to. `portal.sp_Menu_GET_Menu` returns the subtree this user may
 * see for the distributor of the session, so a page granted in the admin
 * appears here without a deploy, and a page revoked disappears.
 *
 * Two facts about the payload drive the code below:
 *
 *  * It is exactly two levels deep. The procedure re-parents orphans to the
 *    root and nests children under `items`, so a group never contains a group.
 *  * `path` is already resolved (state parameters expanded), and `app` says
 *    which application serves it. Choosing the prefix is therefore data, not a
 *    hardcoded list of ported pages.
 */
import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from '@/shared/api/client';
import { legacyRpc } from '@/shared/api/legacy';
import { useAuthStore } from '@/shared/auth/store';
import { jsonValue } from '@/shared/lib/json';
import { LEGACY_BASE, legacyUrl } from './href';
import { withTranslationsEntry } from './translations-entry';
import { withDocumentsEntry } from './documents-entry';

export interface MenuItem {
  id: number;
  icon: string;
  /** `DTK_COMMON_NAVBAR_…` — the caller translates, the API never sends text. */
  labelKey: string;
  /** Application that serves the page, as the catalog tags it. */
  app: string;
  /** Absolute path on this origin. */
  url: string;
  /** True when the page lives in this app and can be routed to client-side. */
  internal: boolean;
}

export interface MenuGroup {
  id: number;
  icon: string;
  labelKey: string;
  items: MenuItem[];
}

/** `webreports.WEBObjectApps.AppCode` of this application. */
const SELF_APP = 'portal-v2';

interface RawMenuItem {
  Id?: number;
  App?: string | null;
  Icon?: string | null;
  LabelKey?: string | null;
  Path?: string | null;
  State?: string | null;
  IsHidden?: boolean;
  Items?: RawMenuItem[] | null;
}

function toItem(raw: RawMenuItem): MenuItem | null {
  const path = (raw.Path ?? '').trim();
  const labelKey = (raw.LabelKey ?? '').trim();
  if (!path || !labelKey) return null;

  const app = (raw.App ?? '').trim() || 'portal-legacy';
  const internal = app === SELF_APP;

  return {
    id: raw.Id ?? 0,
    icon: raw.Icon ?? '',
    labelKey,
    app,
    // An unknown application is treated as legacy: the catalog is edited by
    // hand, and a typo should leave the entry where the page most likely is
    // rather than route it into this app and 404.
    url: internal ? path : `${LEGACY_BASE}${path}`,
    internal,
  };
}

export function parseMenu(rows: RawMenuItem[]): MenuGroup[] {
  const groups: MenuGroup[] = [];

  for (const row of rows) {
    // `isHidden` marks objects that are not menu entries at all — the property
    // container, for one.
    if (row.IsHidden) continue;

    const labelKey = (row.LabelKey ?? '').trim();
    if (!labelKey) continue;

    const items = (row.Items ?? []).map(toItem).filter((i): i is MenuItem => i !== null);
    if (items.length === 0) continue;

    groups.push({ id: row.Id ?? 0, icon: row.Icon ?? '', labelKey, items });
  }

  return withDocumentsEntry(withTranslationsEntry(groups));
}

export async function fetchMenu(): Promise<MenuGroup[]> {
  // No arguments: the procedure takes the user, distributor and client from the
  // token, so the answer already reflects the session's environment and portal
  // type (distributor vs client).
  try {
    const rows = await api<RawMenuItem[]>('/api/v2/menu/items');
    return parseMenu(rows);
  } catch (err) {
    // Each environment runs its own build of the backend, and the request is
    // proxied to the one this session is bound to. On a contour that has not
    // been updated yet the V2 endpoint simply does not exist — falling back to
    // the source the v1 portal uses keeps the menu there instead of leaving the
    // nav bar empty.
    if (err instanceof ApiError && err.status === 404) return fetchLegacyMenu();
    throw err;
  }
}

/** Shape of `sp_Common_GET_UserSettings`, kept only for the fallback above. */
interface LegacyGroup {
  id?: number | string;
  icon?: string;
  val?: string;
  isHidden?: boolean | number;
  items?: unknown;
  [column: string]: unknown;
}

interface LegacyItem {
  id?: number | string;
  icon?: string;
  val?: string;
  /**
   * ui-router state, which in the v1 portal is the URL path, and which can
   * carry parameters as a JavaScript object literal:
   * `/dictionaries({ID:'customers'})`.
   */
  href?: string;
}

async function fetchLegacyMenu(): Promise<MenuGroup[]> {
  const rows = await legacyRpc<LegacyGroup>('SP_Common', 'getUserSettings', {}, { silent: true });

  const groups: MenuGroup[] = [];
  for (const row of rows) {
    if (row.isHidden) continue;
    const labelKey = (row.val ?? '').trim();
    if (!labelKey) continue;

    const items: MenuItem[] = [];
    for (const raw of jsonValue<LegacyItem[]>(row.items) ?? []) {
      const href = (raw.href ?? '').trim();
      const itemLabel = (raw.val ?? '').trim();
      if (!href || !itemLabel) continue;
      items.push({
        id: Number(raw.id ?? 0),
        icon: raw.icon ?? '',
        labelKey: itemLabel,
        app: 'legacy',
        url: legacyUrl(href),
        internal: false,
      });
    }
    if (items.length === 0) continue;

    groups.push({ id: Number(row.id ?? 0), icon: row.icon ?? '', labelKey, items });
  }
  return withDocumentsEntry(withTranslationsEntry(groups));
}

/**
 * Rights and the portal type (distributor vs client) are per distributor, so the
 * menu is cached per session context and refetched when the picker switches.
 */
export function useMenu() {
  const distrId = useAuthStore((s) => s.user?.distrId ?? 0);
  const clientId = useAuthStore((s) => s.user?.clientId ?? 0);
  // Contours number their databases independently, so the same DistrID exists in
  // several of them with different rights. Switching contours reloads the page
  // today, but keying on it costs nothing and keeps that an implementation
  // detail of the switcher rather than a correctness requirement.
  const vaultId = useAuthStore((s) => s.session?.vaultId ?? '');
  const authenticated = useAuthStore((s) => Boolean(s.user));

  return useQuery({
    queryKey: ['menu', vaultId, distrId, clientId],
    queryFn: fetchMenu,
    enabled: authenticated,
    staleTime: 5 * 60 * 1000,
    // The menu frames the whole app: keep the previous tree on screen while the
    // next one loads instead of collapsing the navbar on every switch.
    placeholderData: (prev) => prev,
  });
}
